import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthentication, useFlightRooms, useGetPaginatedFlightRooms, useGetPaginatedTasks } from '@connect-x-app/hybrid-state';
import { Text } from '@connect-x-app/hybrid-ui';
import { FlightRoomSummary, FlightStatus, WorkstationState } from '@connect-x-app/shared-domain-models';
import { hasOpenTasks, isWorkstationEmpty } from '@connect-x-app/shared-utils';
import { FLIGHTROOM_TEST_IDS } from '@connect-x-app/web-test-ids';
import { ModalOpener, Spinner, toastError } from '@connect-x-app/web-ui';
import { hasReachedScrollEnd } from '@connect-x-app/web-utils';
import classNames from 'classnames';

import { FlightroomItem } from './flightroomitem/FlightroomItem';
import { RequestFlightroomAccessModal } from './requestFlightroomAccessModal/RequestFlightroomAccessModal';

type Props = {
  flightRooms: FlightRoomSummary[];
  onFlightRoomClick: (room: FlightRoomSummary) => void;
  workstation: WorkstationState | null;
};

type RoomAccessAction = 'join_requested_room' | 'preview_mode' | 'no_access' | 'default';

export const FlightRoomList: FC<Props> = ({ flightRooms, onFlightRoomClick, workstation }) => {
  const { canJoinFlightRooms, canRequestAccess, canViewFlightRooms, hasPendingAccessRequest, isActiveMemberOfRoom } =
    useAuthentication();
  const { joinFlightRoom, selectedFlightRoom, selectedFlightRoomTab } = useFlightRooms();
  const [isJoining, setIsJoining] = useState<boolean>(false);
  const {
    fetchNextPage: fetchNextPageRooms,
    hasNextPage: hasNextPageRooms,
    isFetchingNextPage: isFetchingNextPageRooms,
    isLoading: isLoadingRooms,
  } = useGetPaginatedFlightRooms(
    selectedFlightRoomTab !== FlightStatus.TASKS ? selectedFlightRoomTab : undefined,
    workstation?.workstationId,
  );

  const {
    fetchNextPage: fetchNextPageTasks,
    hasNextPage: hasNextPageTasks,
    isFetchingNextPage: isFetchingNextPageTasks,
    isLoading: isLoadingTasks,
  } = useGetPaginatedTasks(selectedFlightRoomTab === FlightStatus.TASKS, workstation);

  const { t } = useTranslation();

  const fetchNextPage = useCallback(
    () => (selectedFlightRoomTab === FlightStatus.TASKS ? fetchNextPageTasks() : fetchNextPageRooms()),
    [fetchNextPageRooms, fetchNextPageTasks, selectedFlightRoomTab],
  );

  const hasNextPage = useMemo(
    () => (selectedFlightRoomTab === FlightStatus.TASKS ? hasNextPageTasks : hasNextPageRooms),
    [hasNextPageRooms, hasNextPageTasks, selectedFlightRoomTab],
  );

  const isFetchingNextPage = useMemo(
    () => (selectedFlightRoomTab === FlightStatus.TASKS ? isFetchingNextPageTasks : isFetchingNextPageRooms),
    [selectedFlightRoomTab, isFetchingNextPageRooms, isFetchingNextPageTasks],
  );

  const isLoading = useMemo(
    () => (selectedFlightRoomTab === FlightStatus.TASKS ? isLoadingTasks : isLoadingRooms),
    [selectedFlightRoomTab, isLoadingRooms, isLoadingTasks],
  );

  const determineRoomAccessAction = (room: FlightRoomSummary): RoomAccessAction => {
    if (hasPendingAccessRequest(room) && canJoinFlightRooms) {
      // Edge case where you initially requested access for a room, but in the meantime your permission got updated that you can directly join rooms
      // Ideally this gets updated in the backend whenever there is a role update
      return 'join_requested_room';
    }

    if (hasOpenTasks(room.tasks)) {
      return 'preview_mode';
    }

    if (!isActiveMemberOfRoom(room)) {
      return 'no_access';
    }

    return 'default';
  };

  const onClickFlightRoom = async (room: FlightRoomSummary) => {
    if (room.id === selectedFlightRoom?.id) {
      return;
    }

    const action = determineRoomAccessAction(room);

    if (action === 'join_requested_room') {
      setIsJoining(true);
      const result = await joinFlightRoom(room.id);
      setIsJoining(false);
      if (result?.ok) {
        onFlightRoomClick(room);
      } else {
        toastError(t('TOAST_ERROR'));
      }
      return;
    }

    if (action === 'preview_mode' || action === 'default') {
      onFlightRoomClick(room);
    }
  };

  const onClickTask = async (room: FlightRoomSummary) => {
    if (canJoinFlightRooms) {
      setIsJoining(true);
      const result = await joinFlightRoom(room.id);
      setIsJoining(false);
      if (result?.ok) {
        onFlightRoomClick(room);
      } else {
        toastError(t('TOAST_ERROR'));
      }
      return;
    }

    if (canRequestAccess) {
      ModalOpener.instance.open({
        render: () => (
          <RequestFlightroomAccessModal
            chatroomId={room.id}
            content={t('FLIGHTROOMS.REQUEST_ACCESS.MESSAGE', { flightroom: room.flightNumber })}
            onError={() => toastError(t('TOAST_ERROR'))}
            room={room}
            title={t('FLIGHTROOMS.REQUEST_ACCESS.TITLE')}
          />
        ),
      });
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (hasReachedScrollEnd(e) && !isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  };

  const displayTaskIcon = useMemo(() => {
    return selectedFlightRoomTab === FlightStatus.TASKS;
  }, [selectedFlightRoomTab]);

  const emptyMessage = useMemo(
    // "FLIGHTROOMS.EMPTY_WORKSTATION" Is the text that is shown when there is a workstation but no flightroom that fits into the filters of the workstation
    () => (!isWorkstationEmpty(workstation) ? <Text>{t('FLIGHTROOMS.EMPTY_WORKSTATION')}</Text> : null),
    [t, workstation],
  );

  if (isLoading && canViewFlightRooms) {
    return (
      <section className={classNames('flightrooms-content-container', { 'flightrooms-content-container-loader': isLoading })}>
        <Spinner size="large" />
      </section>
    );
  }

  return (
    <section className="flightrooms-content-container" data-test={FLIGHTROOM_TEST_IDS.list.rooms} onScroll={handleScroll}>
      {flightRooms.length > 0
        ? flightRooms.map((room: FlightRoomSummary) => (
            <FlightroomItem
              disabled={isJoining}
              displayAccessState={true}
              displayJoinIcon={false}
              displayTaskIcon={displayTaskIcon}
              displayUnreadCount={isActiveMemberOfRoom(room)}
              flightroom={room}
              hasUnreadMentions={room.hasUnreadMentions}
              isSelected={room.id === selectedFlightRoom?.id}
              key={room.id}
              onClick={() => onClickFlightRoom(room)}
              onClickTask={() => onClickTask(room)}
              showEstimatedTimes={true}
              showMute={selectedFlightRoomTab === FlightStatus.ACTIVE}
              unreadCount={room.unreadMessagesCount}
            />
          ))
        : emptyMessage}

      {isFetchingNextPage ? (
        <span className="flightrooms-content-fetching-spinner">
          <Spinner size="normal" />
        </span>
      ) : null}
    </section>
  );
};
